import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { GraduationCap, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/Header';
import { Reveal } from '@/components/motion';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, resetPassword, user, userRole, loading } = useAuth();

  useEffect(() => {
    if (!loading && user && userRole) {
      console.log('Redirecting user with role:', userRole);
      if (userRole === 'lecturer') {
        navigate('/lecturer/dashboard');
      } else if (userRole === 'student') {
        navigate('/student/dashboard');
      } else if (userRole === 'admin') {
        navigate('/admin/dashboard');
      } else {
        console.log('Unknown role:', userRole);
        toast({
          title: "Նախազգուշացում",
          description: "Անհայտ օգտվողի տիպ: Դիմեք ադմինիստրատորին",
          variant: "destructive"
        });
      }
    }
  }, [user, userRole, loading, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await signIn(email, password);
      if (!result.error) {
        toast({
          title: "Հաջողություն",
          description: "Դուք հաջողությամբ մուտք գործեցիք"
        });
      } else {
        let errorMessage = "Անսպասելի սխալ";
        console.error('Authentication error details:', result.error);
        if (result.error.message?.includes('Invalid login credentials')) {
          errorMessage = "Սխալ էլ․ փոստ կամ գաղտնաբառ";
        } else if (result.error.message?.includes('Password should be at least')) {
          errorMessage = "Գաղտնաբառը պետք է լինի առնվազն 8 նիշ՝ մեծատառ, փոքրատառ, թիվ և հատուկ նիշով";
        } else if (result.error.message?.includes('rate limit') || result.error.message?.includes('Rate limit')) {
          errorMessage = "Չափազանց շատ փորձություն: Կարող եք փորձել 5 անգամ 15 րոպեում։ Սպասեք և կրկին փորձեք։";
        }
        toast({
          title: "Սխալ",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Unexpected authentication error:', error);
      toast({
        title: "Սխալ",
        description: "Կապի խնդիր: Ստուգեք ինտերնետ կապը և կրկին փորձեք։",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    try {
      const result = await resetPassword(resetEmail);
      if (result.error) {
        let errorMessage = "Անսպասելի սխալ";
        if (result.error.message?.includes('Invalid email')) {
          errorMessage = "Սխալ էլ․ փոստի հասցե";
        } else if (result.error.message?.includes('rate limit')) {
          errorMessage = "Չափազանց շատ հարցումներ: Փորձեք մի փոքր ուշ";
        }
        toast({
          title: "Սխալ",
          description: errorMessage,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Հաջողություն",
          description: "Գաղտնաբառի վերականգնման հղումը ուղարկվել է ձեր էլ․ փոստին"
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast({
        title: "Սխալ",
        description: "Անսպասելի սխալ",
        variant: "destructive"
      });
    }
    setIsResetting(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <div className="flex items-center gap-3 text-sm">
          <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          Բեռնվում է...
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      {/* Ambient brand glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 45% at 15% 0%, hsl(var(--primary) / 0.16), transparent 70%), radial-gradient(50% 40% at 95% 100%, hsl(var(--highlight) / 0.14), transparent 70%)",
        }}
      />

      <Header />

      <main className="flex flex-1 items-center justify-center px-4 pb-12 pt-[96px]">
        <Reveal className="w-full max-w-md">
          <Card className="border-border/70 bg-card/80 shadow-[var(--shadow-elevated)] backdrop-blur-sm">
            <CardHeader className="space-y-3 text-center">
              <div className="flex justify-center">
                <div className="rounded-2xl bg-secondary p-3 text-primary ring-1 ring-primary/20">
                  <GraduationCap className="h-7 w-7" aria-hidden="true" />
                </div>
              </div>
              <CardTitle className="text-2xl font-semibold tracking-tight text-foreground">
                Մուտք
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Մուտք գործեք ձեր ուսանողական, դասախոսի կամ ադմինիստրատորի հաշիվ
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    Էլ. փոստ
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground">
                    Գաղտնաբառ
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>

                <Button type="submit" className="h-11 w-full text-base" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Բեռնվում է...
                    </>
                  ) : (
                    "Մուտք գործել"
                  )}
                </Button>

                {!showForgotPassword && (
                  <div className="text-center">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setShowForgotPassword(true)}
                      className="h-auto whitespace-normal text-sm text-muted-foreground hover:text-primary"
                    >
                      Մոռացե՞լ եք գաղտնաբառը
                    </Button>
                  </div>
                )}
              </form>

              {showForgotPassword && (
                <div className="rounded-xl border border-border/70 bg-muted/40 p-4">
                  <h3 className="mb-4 text-base font-semibold text-foreground">Գաղտնաբառի վերականգնում</h3>
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="resetEmail" className="text-foreground">
                        Էլ. փոստ
                      </Label>
                      <Input
                        id="resetEmail"
                        type="email"
                        autoComplete="email"
                        value={resetEmail}
                        onChange={e => setResetEmail(e.target.value)}
                        required
                        className="h-11"
                      />
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button type="submit" className="h-11 w-full sm:flex-1" disabled={isResetting}>
                        {isResetting ? "Ուղարկվում է..." : "Ուղարկել"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="h-11 w-full sm:w-auto"
                      >
                        Չեղարկել
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </CardContent>
          </Card>
        </Reveal>
      </main>
    </div>
  );
};

export default Login;
